import type { FeatureCollection } from "geojson";
import { type ComponentType, useEffect, useMemo } from "react";

import { t } from "../../i18n/i18n";
import { useUi } from "../../state/ui";
import { useShownDeployment } from "../manoeuvre/useOrientation";
import { revealPaint, useMapRevealed } from "../reveal";
import { chiefChanges, chiefNoticeSeen, chiefTitle, markChiefNoticeSeen } from "./chiefNotice";
import { annotationImageId } from "./symbolStyle";
import { annotationFeatures, annotationsOf } from "./useAnnotations";

/** Annotations APP-6 sur la carte (calque « Annotations », exception à la carte figée accordée par Alexis le
 *  15/10/2026) : symboles milsymbol au temps affiché (positions par temps, temps cochés dans la fiche), un calque
 *  masquable par catégorie, nom sous le symbole ; l'annotation sélectionnée est cernée de blanc. Au-dessus des
 *  graphiques et sous les unités ; le symbole porté par le pointeur pendant une pose (`draft` « annotation ») au
 *  -dessus de tout, estompé. */

interface EngineParts {
  Source: ComponentType<any>;
  Layer: ComponentType<any>;
}

export const ANNOTATION_LAYER_ID = "annotations-symbol";
const FONT = ["Open Sans Semibold"];
const EMPTY: FeatureCollection = { type: "FeatureCollection", features: [] };

export function AnnotationsLayers({ Source, Layer }: EngineParts) {
  const { data } = useShownDeployment();
  const phase = useUi((s) => s.phase);
  const controls = useUi((s) => s.controlVisible);
  const shift = useUi((s) => s.annotationShift);
  const selection = useUi((s) => s.selection);
  const revealed = useMapRevealed();
  const selectedId = selection?.layer === "annotation" ? selection.id : "";
  const collection = useMemo(
    () => annotationFeatures(annotationsOf(data), phase, controls, shift),
    [data, phase, controls, shift],
  );
  // Annotations reprises du chef changées depuis la dernière visite : avis une fois sur ce poste.
  const exerciseId = useUi((s) => s.deploymentParams?.exercise_id ?? null);
  const chief = data?.chief_annotations ?? null;
  useEffect(() => {
    if (!exerciseId || !chief || chiefNoticeSeen(exerciseId, chief)) return;
    markChiefNoticeSeen(exerciseId, chief);
    const changes = chiefChanges(chief);
    useUi.getState().notify({
      text: chiefTitle(chief),
      detail: changes
        ? t("Annotations {changes}, reprises sur votre carte en lecture seule.", { changes })
        : t("Ses annotations sont reprises sur votre carte en lecture seule."),
      tone: "info",
    });
  }, [exerciseId, chief]);
  const isSelected = ["==", ["get", "id"], selectedId];
  return (
    <Source id="gx-annotations" type="geojson" data={collection}>
      <Layer
        id="annotations-halo"
        type="circle"
        filter={isSelected}
        paint={revealPaint(
          "circle",
          { "circle-radius": 24, "circle-color": "#ffffff", "circle-opacity": 0.55, "circle-stroke-color": "#ffffff", "circle-stroke-width": 2 },
          revealed,
        )}
      />
      <Layer
        id={ANNOTATION_LAYER_ID}
        type="symbol"
        layout={{ "icon-image": ["get", "icon"], "icon-allow-overlap": true, "icon-ignore-placement": true }}
        paint={revealPaint("symbol", {}, revealed)}
      />
      {/* Nom sous le symbole, dans une couche à part : une police de carte indisponible ne cache pas les symboles. */}
      <Layer
        id="annotations-label"
        type="symbol"
        layout={{
          "text-field": ["get", "label"],
          "text-font": FONT,
          "text-size": 11,
          "text-anchor": "top",
          "text-offset": [0, 1.9],
          "text-optional": true,
        }}
        paint={revealPaint("symbol", { "text-color": "#1f2430", "text-halo-color": "#ffffff", "text-halo-width": 1.4 }, revealed)}
      />
    </Source>
  );
}

/** Symbole porté par le pointeur pendant la pose d'une annotation. */
export function AnnotationDraftLayer({ Source, Layer }: EngineParts) {
  const draft = useUi((s) => s.draft);
  const data = useMemo<FeatureCollection>(() => {
    if (!draft || draft.family !== "annotation" || !draft.hover || !draft.sidc) return EMPTY;
    return {
      type: "FeatureCollection",
      features: [
        { type: "Feature", geometry: { type: "Point", coordinates: draft.hover }, properties: { icon: annotationImageId(draft.sidc) } },
      ],
    };
  }, [draft]);
  return (
    <Source id="gx-annotations-draft" type="geojson" data={data}>
      <Layer
        id="annotations-draft"
        type="symbol"
        layout={{ "icon-image": ["get", "icon"], "icon-allow-overlap": true, "icon-ignore-placement": true }}
        paint={{ "icon-opacity": 0.65 }}
      />
    </Source>
  );
}
